/**
 * iOS Dynamic Type detector.
 *
 * O iOS/Safari resolve `font: -apple-system-body` para o tamanho de fonte
 * escolhido pelo usuário em Ajustes > Tela e Brilho > Tamanho do Texto (e no
 * modo "Texto Maior" em Acessibilidade). Medimos essa fonte contra a base
 * padrão (17px) para derivar um multiplicador — `--ios-dt-scale` — que outros
 * componentes podem aplicar em tipografia, line-height, spacing e tap targets.
 *
 * Em plataformas que não expõem `-apple-system-body` (Android/desktop),
 * caímos no `prefers-reduced-*`/zoom nativo do próprio navegador; nesse caso
 * a escala fica em 1 e nada muda.
 *
 * Clampeamos [0.85, 1.6] para evitar quebrar layouts em telas muito estreitas
 * quando o usuário escolhe o AX3+ (que pode chegar a ~2.35 no iOS).
 */

const MIN_SCALE = 0.85;
const MAX_SCALE = 1.6;
const BASE_SIZE = 17; // Padrão do iOS para body.

let installed = false;

function measureIosBodyScale(): number {
  if (typeof document === "undefined") return 1;
  const probe = document.createElement("span");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText = [
    "position:absolute",
    "visibility:hidden",
    "pointer-events:none",
    "top:-9999px",
    "left:-9999px",
    "font: -apple-system-body",
  ].join(";");
  probe.textContent = "A";
  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).fontSize;
  document.body.removeChild(probe);
  const px = parseFloat(computed);
  if (!Number.isFinite(px) || px <= 0) return 1;
  const scale = px / BASE_SIZE;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

function apply(scale: number) {
  const root = document.documentElement;
  root.style.setProperty("--ios-dt-scale", scale.toFixed(3));
  // Marca de conveniência para debugging/observabilidade.
  root.dataset.dtScale = scale.toFixed(2);
  if (scale >= 1.25) root.dataset.dtLarge = "true";
  else delete root.dataset.dtLarge;
}

export function installDynamicType() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const run = () => {
    try {
      apply(measureIosBodyScale());
    } catch {
      apply(1);
    }
  };

  run();

  // iOS pode mudar a preferência sem recarregar (retorno de Ajustes,
  // rotação, mudança de "Modo Zoom"). Reamostramos em eventos baratos.
  window.addEventListener("pageshow", run);
  window.addEventListener("focus", run);
  window.addEventListener("orientationchange", run);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
}
